import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Subscription } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const GracePeriod = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`p-2 ${loading ? 'opacity-50' : 'opacity-100'}`}>
      <div className="px-6 py-4 flex items-center justify-between bg-[#FFF0CC]">
        <div className="flex items-start gap-3">
          {/* Warning triangle icon */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#D97706"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 mt-0.5"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>

          <div>
            <p className="text-[#434343] font-bold text-lg leading-tight">
              Payment Overdue - Grace Period Active
            </p>
            <p className="text-[#434343F2] text-sm mt-0.5">
              Your account access will be restricted after the grace period
              expires
            </p>
          </div>
        </div>

        <div className="text-right flex-shrink-0 ml-6">
          <p className="text-[#434343E5] text-sm font-medium uppercase tracking-wide leading-tight">
            ACCOUNT WILL BE
          </p>
          <p className="text-[#434343E5] font-bold text-sm uppercase tracking-wide">
            SUSPENDED ON{' '}
            {handleDateFormat(subscriptionDetails?.nextBillingDate)}
          </p>
        </div>
      </div>
      <div className="flex gap-4 mt-5">
        {/* Left Card — Outstanding Payment */}
        <div className="flex-1 border border-[#E8E8E8] rounded-2xl p-6 flex flex-col gap-5">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-[#1A1A1A] text-xl font-semibold">
                Outstanding Payment
              </h3>
              <p className="text-[#1A1A1A] text-sm mt-0.5">
                As of {handleDateFormat(subscriptionDetails?.lastPaymentDate)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(FrontendRoutes.SUBSCRIPTION_REACTIVATE)}
              className="flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors whitespace-nowrap"
            >
              <svg
                width="17"
                height="14"
                viewBox="0 0 17 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.6667 1.66667V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8437C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667C1.20833 13.3333 0.815972 13.1701 0.489583 12.8437C0.163194 12.5174 0 12.125 0 11.6667V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H15C15.4583 0 15.8507 0.163194 16.1771 0.489583C16.5035 0.815972 16.6667 1.20833 16.6667 1.66667ZM1.66667 3.33333H15V1.66667H1.66667V3.33333ZM1.66667 6.66667V11.6667H15V6.66667H1.66667Z"
                  fill="white"
                />
              </svg>
              Reactivate
            </button>
          </div>

          {/* Subscription Fee */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[#1A1A1A] text-sm">Subscription Fee</p>
              <p className="text-[#6C757D] text-sm mt-0.5">
                {subscriptionDetails?.orderSpeciesCount} animals × ${' '}
                {subscriptionDetails?.price} per animal
              </p>
            </div>
            <span className="text-[#6C757D] text-base font-medium">
              ${subscriptionDetails?.estimatedCost.toFixed(2)}
            </span>
          </div>

          {/* Discount Applied */}
          {subscriptionDetails?.promoCodes
            ?.filter((promo) => promo.isActive === 1)
            .map((promo) => (
              <div key={promo.id} className="flex items-start justify-between">
                <div>
                  <p className="text-[#1A1A1A] text-sm">Discount Applied</p>
                  <p className="text-[#6C757D] text-sm mt-0.5">
                    {promo.PromoCodes.description}
                  </p>
                </div>
                <span className="text-[#10B981] text-base font-bold">
                  -${subscriptionDetails?.discountedPrice.toFixed(2)}
                </span>
              </div>
            ))}

          {/* Total */}
          <div className="bg-white px-5 py-4 flex items-center justify-between">
            <span className="text-gray-900 font-bold text-lg">Total</span>
            <span className="text-[#2D5A3D] font-bold text-3xl">
              ${subscriptionDetails?.estimatedCostWithDiscount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Right Card — What Happens Next */}
        <div className="flex-1 border border-[#E8E8E8] rounded-2xl p-6 flex flex-col gap-5">
          <h3 className="text-[#1A1A1A] text-xl font-semibold">
            What Happens Next?
          </h3>

          {/* Steps */}
          <div className="flex flex-col gap-4 flex-1">
            {/* Grace period ends */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 text-amber-600">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <span className="text-[#6C757D] text-sm">
                Grace period ends in 3 days
              </span>
            </div>

            {/* All features locked */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 text-amber-600">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <span className="text-[#6C757D] text-sm">
                All features will be locked
              </span>
            </div>

            {/* Only payment page accessible */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 text-amber-600">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <span className="text-[#6C757D] text-sm">
                Only payment page accessible
              </span>
            </div>

            {/* All access suspended */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 text-amber-600">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <span className="text-[#6C757D] text-sm">
                All access suspended
              </span>
            </div>
          </div>

          {/* Make Payment Now Button */}
          <button
            type="button"
            onClick={() => navigate(FrontendRoutes.SUBSCRIPTION_REACTIVATE)}
            className="flex items-center justify-center gap-2 bg-primary text-white font-semibold text-base px-5 py-3.5 rounded-md transition-colors w-full"
          >
            <svg
              width="17"
              height="14"
              viewBox="0 0 17 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6667 1.66667V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8437C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667C1.20833 13.3333 0.815972 13.1701 0.489583 12.8437C0.163194 12.5174 0 12.125 0 11.6667V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H15C15.4583 0 15.8507 0.163194 16.1771 0.489583C16.5035 0.815972 16.6667 1.20833 16.6667 1.66667ZM1.66667 3.33333H15V1.66667H1.66667V3.33333ZM1.66667 6.66667V11.6667H15V6.66667H1.66667Z"
                fill="white"
              />
            </svg>
            Reactivate Subscription Now
          </button>
        </div>
      </div>
    </div>
  );
};
